/**
 * `planWatchOrder` — the time-budget solver, PLAN §3. The differentiating
 * feature of the whole project.
 *
 * You give it a deadline and the hours a week you actually have. It gives you
 * an ordered plan that fits, and an honest account of everything it dropped to
 * make it fit. The eight steps of PLAN §3 are the eight sections below, in
 * order:
 *
 * 0. hard filters, each rejection a typed {@link Cut}
 * 1. split into legs (Encore mode)
 * 2. capacity, discounted by a slack factor
 * 3. per-entry cost — the shortest curated route when alternatives are on
 * 4. the weighted value function ({@link ./score.ts})
 * 5. the mandatory set: every surviving essential, never truncated
 * 6. knapsack fill with prerequisite bundling ({@link ./knapsack.ts})
 * 7. forward bin-packing into weekly buckets
 * 8. the cut list, biggest sacrifice first
 *
 * **Pure, and deliberately calendar-based.** The input carries `today` as an
 * ISO date, not an epoch — every date here is whole calendar days computed in
 * UTC, so the plan a user gets in Auckland is the plan the fixtures assert and
 * the plan the Swift port produces. (The ticking countdown in
 * `lib/countdown/legs.ts` is the opposite trade: local instants, because a
 * clock has to agree with the wall.)
 *
 * **Determinism is a feature, not an accident.** Every float is rounded to
 * four decimals before comparison, every ordering is
 * `(value desc, cost asc, orderIndex asc)`, and the result carries a
 * {@link PlanResult.deterministicHash} over the inputs and the plan. The
 * fixtures in `src/fixtures/solver/` assert that hash — they are the parity
 * contract the SwiftUI port has to satisfy.
 */

import type {
  Catalog,
  Entry,
  EntryId,
  ISODate,
  Milestone,
  RuntimeAlternative,
  Tier,
} from "@schema/catalog.ts";
import { type ResolvedFilters, resolveFilters } from "@/lib/filters/apply.ts";
import type { FilterState } from "@/lib/filters/types.ts";
import {
  type FallbackCause,
  type KnapsackBundle,
  type KnapsackUnit,
  solveKnapsack,
} from "./knapsack.ts";
import {
  chooseRoute,
  compressibleMinutes,
  entryValue,
  type Route,
  roundTo,
  type ScoreContext,
} from "./score.ts";
import { normalizeSlack, normalizeWeights, type SolverWeights, WEIGHT_KEYS } from "./weights.ts";

/** Bumped when the algorithm changes in a way that moves plans. Part of the hash. */
export const SOLVER_VERSION = 1;

const MINUTES_PER_HOUR = 60;
const DAYS_PER_WEEK = 7;
const MS_PER_DAY = 86_400_000;

// ---------------------------------------------------------------------------
// Calendar helpers — UTC only, see the module header.
// ---------------------------------------------------------------------------

function toUtcMs(date: ISODate): number {
  const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function fromUtcMs(ms: number): ISODate {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to`; negative when `to` is in the past. */
export function daysBetween(from: ISODate, to: ISODate): number {
  return Math.round((toUtcMs(to) - toUtcMs(from)) / MS_PER_DAY);
}

export function addDays(date: ISODate, days: number): ISODate {
  return fromUtcMs(toUtcMs(date) + days * MS_PER_DAY);
}

const laterDate = (a: ISODate, b: ISODate): ISODate => (toUtcMs(b) > toUtcMs(a) ? b : a);
const earlierDate = (a: ISODate, b: ISODate): ISODate => (toUtcMs(b) < toUtcMs(a) ? b : a);

/** Ask for a believable number of hours — half-hour granularity, always rounded up. */
const roundUpToHalf = (hours: number): number => Math.ceil(hours * 2) / 2;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** `"road"` with Encore mode off; `"leg-1"` / `"leg-2"` with it on. Matches `lib/countdown`. */
export type LegId = "road" | "leg-1" | "leg-2";

/**
 * Why an entry isn't in your plan. The first six are step 0's hard filters —
 * you ruled it out — and `over-budget` is the solver's own doing: it survived
 * every filter and still didn't fit. The UI leads with the `over-budget` ones,
 * because those are the sacrifices you didn't choose.
 */
export type CutReason =
  | "tier-ceiling"
  | "series-excluded"
  | "quality-floor"
  | "runtime-cap"
  | "character-filter"
  | "thread-filter"
  | "over-budget";

/** One line of the cut list (PLAN §3, step 8). */
export interface Cut {
  entryId: EntryId;
  title: string;
  orderIndex: number;
  leg: 1 | 2;
  legId: LegId;
  tier: Tier;
  reason: CutReason;
  costMinutes: number;
  /** `costMinutes / 60`, rounded to two decimals — the number the UI prints. */
  costHours: number;
  /** What it scored. The cut list is sorted on this, descending. */
  value: number;
  /** `curation.whatYouMissIfSkipped` — the honest part. */
  whatYouMiss: string;
}

/**
 * A way out of an infeasible leg, in the order PLAN §3 step 5 puts them:
 * take the shortcuts, then raise the pace, then give up the nearer deadline,
 * then narrow what counts.
 *
 * `sufficient` says whether the remedy closes the gap **on its own**. Note the
 * asymmetry on the last one: a leg is infeasible only when the *essentials*
 * overflow, and no tier ceiling can drop an essential — so lowering the
 * ceiling can only ever shed the non-essential prerequisites they dragged in
 * with them. It is `sufficient: false` and `savedMinutes: 0` more often than
 * not, and the UI should say so rather than imply the slider will save you.
 * When nothing here is sufficient, `LegPlan.deficitMinutes` is the real
 * number: that many minutes of essential viewing does not fit, and the only
 * remaining move is to accept it.
 */
export type Remedy =
  | { kind: "enable-alternatives"; savedMinutes: number; sufficient: boolean }
  | { kind: "raise-pace"; hoursPerWeekNeeded: number; sufficient: boolean }
  | { kind: "disable-encore"; extraDays: number; sufficient: boolean }
  | {
      kind: "lower-tier-ceiling";
      /** The `TierDef.rank` ceiling this remedy proposes — always 1, essentials only. */
      tierMax: number;
      savedMinutes: number;
      droppedCount: number;
      sufficient: boolean;
    };

/** The compressed route an entry is planned at, flattened for the UI and for JSON. */
export interface PlannedRoute {
  kind: RuntimeAlternative["kind"];
  label: string;
  minutes: number;
  valueRetention: number;
}

/** One entry in the plan. */
export interface PlannedEntry {
  entryId: EntryId;
  title: string;
  orderIndex: number;
  leg: 1 | 2;
  tier: Tier;
  /** What it costs at the route it's planned at. */
  costMinutes: number;
  value: number;
  /** A surviving essential, or something an essential requires. Never cut. */
  mandatory: boolean;
  /** Pulled in as a prerequisite of another planned entry rather than on its own merits. */
  prerequisite: boolean;
  compressed: boolean;
  route: PlannedRoute | null;
  /** 1-based index into {@link LegPlan.weeks}. */
  weekIndex: number;
}

/**
 * One entry's share of one week. An entry longer than what's left of a week
 * continues into the next, so it appears once per week it spans — with
 * `continued`/`continues` telling the timeline which end to draw open.
 */
export interface WeekItem {
  entryId: EntryId;
  /** Minutes of this entry that land in this week. */
  minutes: number;
  /** It started in an earlier week. */
  continued: boolean;
  /** It finishes in a later week. */
  continues: boolean;
}

/** One weekly bucket of the schedule (PLAN §3, step 7). */
export interface WeekPlan {
  /** 1-based. */
  index: number;
  startDate: ISODate;
  /** Seven days on, except for a final partial week, which ends on the deadline. */
  endDate: ISODate;
  minutes: number;
  items: WeekItem[];
}

/** How the fill was solved. The first three are optimal; the fourth means there was no fill. */
export type PlanMethod = "take-all" | "exact-dp" | "greedy-swap" | "mandatory-only";

/** One leg: a scope of entries, a window of days, and the plan that fits in it. */
export interface LegPlan {
  id: LegId;
  /** The event this leg is due for — Doomsday, or the Encore. */
  eventTitle: string;
  /** First day of this leg's window: today, or the Encore date for leg 2. */
  startDate: ISODate;
  deadline: ISODate;
  /** Whole calendar days in the window. The deadline day itself counts as 0. */
  daysLeft: number;
  hoursPerWeek: number;
  slack: number;
  /** `daysLeft × (hoursPerWeek/7) × 60 × (1 − slack)`, floored to whole minutes. */
  capacityMinutes: number;
  /** False when the surviving essentials alone don't fit. */
  feasible: boolean;
  method: PlanMethod;
  /** The fill is provably optimal (as opposed to the greedy fallback). */
  exact: boolean;
  /** Entries that survived step 0 in this leg. */
  candidateCount: number;
  mandatoryCount: number;
  mandatoryMinutes: number;
  /** In road order — this is the watch order. */
  selected: PlannedEntry[];
  selectedCount: number;
  selectedMinutes: number;
  selectedValue: number;
  /** Capacity left on the table; 0 when the leg is full or over. */
  unusedMinutes: number;
  /** How far past capacity an infeasible leg runs; 0 otherwise. */
  deficitMinutes: number;
  /** `selectedMinutes / 60 / daysLeft`; `null` when the window has no days left. */
  requiredHoursPerDay: number | null;
  /** `requiredHoursPerDay / (hoursPerWeek/7)`; `null` when it can't be computed. */
  utilization: number | null;
  weeks: WeekPlan[];
  /** Last day of the last scheduled week; `null` when nothing is planned. */
  projectedFinish: ISODate | null;
  /** Empty unless `feasible === false`. */
  remedies: Remedy[];
  /** This leg's share of the cut list, biggest sacrifice first. */
  cuts: Cut[];
}

/** Everything the plan needs to admit to. */
export type PlanWarning =
  | {
      kind: "approximate-fill";
      legId: LegId;
      cause: FallbackCause;
      /** Entries that appear in more than one prerequisite bundle. */
      overlappingEntryIds: EntryId[];
    }
  | { kind: "infeasible-leg"; legId: LegId; deficitMinutes: number; mandatoryCount: number }
  | { kind: "unmet-prerequisites"; legId: LegId; entryId: EntryId; missing: EntryId[] }
  | { kind: "no-days-left"; legId: LegId }
  /**
   * The plan fits the leg's *minutes* but not its *weeks*. Capacity is a
   * single pool of minutes; the schedule is whole weekly buckets and never
   * splits an entry across two of them, so one 306-minute season against a
   * 135-minute week takes a week to itself and wastes the rest. A leg can
   * therefore be feasible and still finish late — which the plan has to admit
   * rather than let `projectedFinish` quietly contradict the deadline.
   */
  | {
      kind: "schedule-overruns-deadline";
      legId: LegId;
      projectedFinish: ISODate;
      overrunDays: number;
    };

export interface PlanTotals {
  /** Entries that survived step 0, across every leg. */
  candidateCount: number;
  selectedCount: number;
  selectedMinutes: number;
  /** `selectedMinutes / 60`, two decimals. */
  selectedHours: number;
  capacityMinutes: number;
  cutCount: number;
  cutMinutes: number;
  watchedCount: number;
  watchedMinutes: number;
}

export interface PlanResult {
  solverVersion: number;
  /** The day the plan was computed for. Every date in the plan derives from it. */
  today: ISODate;
  /** Encore mode was on **and** the catalog has a splitting milestone. */
  encore: boolean;
  hoursPerWeek: number;
  slack: number;
  /** The weights actually used, after defaults and clamping. */
  weights: SolverWeights;
  /** Every leg fits. */
  feasible: boolean;
  legs: LegPlan[];
  /** The whole cut list across legs, biggest sacrifice first (PLAN §3, step 8). */
  cuts: Cut[];
  warnings: PlanWarning[];
  totals: PlanTotals;
  /** FNV-1a over the inputs and the plan. Asserted by the parity fixtures. */
  deterministicHash: string;
}

export interface SolverInput {
  catalog: Catalog;
  /** Step 0. The same `FilterState` the URL and the road already use. */
  filters: FilterState;
  /** The day to plan from, as an ISO date. */
  today: ISODate;
  hoursPerWeek: number;
  encoreMode: boolean;
  /**
   * Already-watched entries, from the progress store. They cost nothing, are
   * never planned, and are never cut — you didn't sacrifice them, you did them.
   * They do satisfy prerequisites.
   */
  watched?: Readonly<Record<EntryId, ISODate>>;
  /** Advanced drawer overrides; anything omitted keeps its default. */
  weights?: Partial<SolverWeights>;
  /** Capacity discount, default {@link DEFAULT_SLACK}. */
  slack?: number;
}

// ---------------------------------------------------------------------------
// Step 0 — hard filters
// ---------------------------------------------------------------------------

/**
 * Which rule rejected this entry, or `null` if none did.
 *
 * The rules and their order mirror `matchesFilters` in `lib/filters/apply.ts`
 * exactly — that function answers "is it on the road?" for rendering, this one
 * answers "and why not?" for the cut list, and a test asserts the two never
 * disagree. Essentials bypass the quality floor while
 * `essentialsIgnoreQuality` is on: quality is a separate axis from importance,
 * and the gate must not be able to cut an essential by accident.
 */
export function hardFilterReason(entry: Entry, resolved: ResolvedFilters): CutReason | null {
  const { filters } = resolved;

  const rank = resolved.tierRank.get(entry.curation.tier) ?? Number.POSITIVE_INFINITY;
  if (rank > filters.tierMax) return "tier-ceiling";

  if (!filters.includeSeries && entry.kind === "season") return "series-excluded";

  const exempt = filters.essentialsIgnoreQuality && entry.curation.tier === "essential";
  // Mirrors matchesFilters in lib/filters/apply.ts, including the null case:
  // an unrated entry is not below the floor, it is unmeasured against it.
  const score = entry.quality.score;
  if (!exempt && score !== null && score < filters.qualityMin) return "quality-floor";

  if (filters.maxMinutes !== null) {
    const minutes = chooseRoute(entry, filters.useAlternatives).minutes;
    if (minutes > filters.maxMinutes) return "runtime-cap";
  }

  if (resolved.characters.size > 0) {
    if (!entry.curation.characters.some((link) => resolved.characters.has(link.id))) {
      return "character-filter";
    }
  }

  if (resolved.threads.size > 0) {
    if (!entry.curation.threads.some((link) => resolved.threads.has(link.id))) {
      return "thread-filter";
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Candidates — steps 3 and 4 applied to one entry
// ---------------------------------------------------------------------------

interface Candidate {
  entry: Entry;
  route: Route;
  cost: number;
  value: number;
  orderIndex: number;
  legId: LegId;
}

function toPlannedRoute(route: Route): PlannedRoute | null {
  if (!route.alternative) return null;
  return {
    kind: route.alternative.kind,
    label: route.alternative.label,
    minutes: route.alternative.minutes,
    valueRetention: route.alternative.valueRetention,
  };
}

function makeCut(candidate: Candidate, reason: CutReason): Cut {
  const { entry } = candidate;
  return {
    entryId: entry.id,
    title: entry.title,
    orderIndex: entry.orderIndex,
    leg: entry.leg,
    legId: candidate.legId,
    tier: entry.curation.tier,
    reason,
    costMinutes: candidate.cost,
    costHours: roundTo(candidate.cost / MINUTES_PER_HOUR, 2),
    value: candidate.value,
    whatYouMiss: entry.curation.whatYouMissIfSkipped,
  };
}

/** Cut list order: biggest sacrifice first, then road order for a stable tail. */
function compareCuts(a: Cut, b: Cut): number {
  if (a.value !== b.value) return b.value - a.value;
  if (a.costMinutes !== b.costMinutes) return b.costMinutes - a.costMinutes;
  return a.orderIndex - b.orderIndex;
}

// ---------------------------------------------------------------------------
// Steps 5 and 6 — the mandatory set and the fill
// ---------------------------------------------------------------------------

/**
 * `seeds` plus everything they transitively require, restricted to entries
 * that are actually available (survived step 0, in this leg, not watched).
 *
 * The restriction is the point: a prerequisite you filtered out or already
 * watched stops the walk rather than dragging a filtered entry back into the
 * plan. What it can't do is make the dependency go away, so anything still
 * missing is reported as an `unmet-prerequisites` warning once every leg is
 * solved.
 */
function closureWithin(
  seeds: readonly EntryId[],
  available: ReadonlyMap<EntryId, Candidate>,
): Set<EntryId> {
  const found = new Set<EntryId>();
  const queue = [...seeds];
  while (queue.length > 0) {
    const id = queue.pop();
    if (id === undefined || found.has(id)) continue;
    const candidate = available.get(id);
    if (!candidate) continue;
    found.add(id);
    for (const prerequisite of candidate.entry.curation.prerequisites) {
      if (!found.has(prerequisite)) queue.push(prerequisite);
    }
  }
  return found;
}

interface LegSolution {
  plan: LegPlan;
  /** Everything selected, mandatory and filled alike. */
  selectedIds: Set<EntryId>;
  mandatoryIds: Set<EntryId>;
  /** Minutes the mandatory set would give back on its shortest curated routes. */
  mandatoryCompressible: number;
  /** Minutes of the mandatory set that aren't themselves essential. */
  mandatoryNonEssentialMinutes: number;
  mandatoryNonEssentialCount: number;
  fallback: { cause: FallbackCause; overlapping: EntryId[] } | null;
}

interface LegSetup {
  id: LegId;
  eventTitle: string;
  startDate: ISODate;
  deadline: ISODate;
  candidates: Candidate[];
  hoursPerWeek: number;
  slack: number;
  useAlternatives: boolean;
}

function solveLeg(setup: LegSetup): LegSolution {
  const daysLeft = Math.max(0, daysBetween(setup.startDate, setup.deadline));
  const availableHoursPerDay = setup.hoursPerWeek / DAYS_PER_WEEK;

  // Step 2 — capacity, with the slack factor.
  const capacityMinutes = Math.max(
    0,
    Math.floor(daysLeft * availableHoursPerDay * MINUTES_PER_HOUR * (1 - setup.slack)),
  );

  const byId = new Map(
    setup.candidates.map((candidate) => [candidate.entry.id, candidate] as const),
  );

  // Step 5 — the mandatory set: every surviving essential, plus the surviving
  // entries they require. A plan that says "watch Endgame" but drops what
  // Endgame is built on is not a plan.
  const essentials = setup.candidates
    .filter((candidate) => candidate.entry.curation.tier === "essential")
    .map((candidate) => candidate.entry.id);
  const mandatoryIds = closureWithin(essentials, byId);

  let mandatoryMinutes = 0;
  let mandatoryCompressible = 0;
  let mandatoryNonEssentialMinutes = 0;
  let mandatoryNonEssentialCount = 0;
  for (const id of mandatoryIds) {
    const candidate = byId.get(id);
    if (!candidate) continue;
    mandatoryMinutes += candidate.cost;
    if (!setup.useAlternatives) mandatoryCompressible += compressibleMinutes(candidate.entry);
    if (candidate.entry.curation.tier !== "essential") {
      mandatoryNonEssentialMinutes += candidate.cost;
      mandatoryNonEssentialCount += 1;
    }
  }

  const feasible = mandatoryMinutes <= capacityMinutes;

  const selectedIds = new Set(mandatoryIds);
  let method: PlanMethod = "mandatory-only";
  let fallback: LegSolution["fallback"] = null;

  // Step 6 — fill what's left. Skipped entirely for an infeasible leg: the
  // essentials already overflow, and adding optional viewing on top of an
  // overflow would be dishonest about what "planned" means.
  if (feasible) {
    const fillCandidates = setup.candidates.filter(
      (candidate) => !mandatoryIds.has(candidate.entry.id),
    );
    const fillById = new Map(
      fillCandidates.map((candidate) => [candidate.entry.id, candidate] as const),
    );
    const units = new Map<EntryId, KnapsackUnit>(
      fillCandidates.map((candidate) => [
        candidate.entry.id,
        {
          id: candidate.entry.id,
          cost: candidate.cost,
          value: candidate.value,
          orderIndex: candidate.orderIndex,
        },
      ]),
    );

    const bundles: KnapsackBundle[] = fillCandidates.map((candidate) => {
      // Road order, not traversal order: the members are summed into a float,
      // and a sum is only reproducible in another language if the terms are
      // added in the same sequence.
      const members = [...closureWithin([candidate.entry.id], fillById)].sort(
        (a, b) => (fillById.get(a)?.orderIndex ?? 0) - (fillById.get(b)?.orderIndex ?? 0),
      );
      let cost = 0;
      let value = 0;
      for (const member of members) {
        const unit = units.get(member);
        if (!unit) continue;
        cost += unit.cost;
        value += unit.value;
      }
      return {
        id: candidate.entry.id,
        members,
        cost,
        value: roundTo(value),
        orderIndex: candidate.orderIndex,
      };
    });

    const filled = solveKnapsack({
      bundles,
      units,
      capacity: capacityMinutes - mandatoryMinutes,
    });
    for (const id of filled.members) selectedIds.add(id);
    method = filled.method;
    if (filled.fallbackCause) {
      fallback = { cause: filled.fallbackCause, overlapping: filled.overlapping };
    }
  }

  // Step 7 — schedule. Road order is the watch order, packed forward into
  // weekly buckets of `hoursPerWeek × 60 × (1 − slack)` minutes.
  //
  // A long entry spills into the following week rather than claiming one to
  // itself. That is the difference between a schedule that agrees with the
  // capacity arithmetic and one that quietly contradicts it: a 306-minute
  // season against a 135-minute week would otherwise waste 171 minutes of a
  // budget the solver has already spent, and a plan that provably fits by
  // minutes would report finishing weeks after the deadline. Weeks are a
  // rendering of your pace, not an instruction to watch a season in one go.
  const selected = [...selectedIds]
    .map((id) => byId.get(id))
    .filter((candidate): candidate is Candidate => candidate !== undefined)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const weeklyMinutes = Math.max(
    0,
    Math.floor(setup.hoursPerWeek * MINUTES_PER_HOUR * (1 - setup.slack)),
  );
  const weeks: WeekPlan[] = [];
  const planned: PlannedEntry[] = [];
  let selectedMinutes = 0;
  let selectedValue = 0;

  /** Open buckets up to `index` (1-based) and return that one. */
  const weekAt = (index: number): WeekPlan => {
    let week = weeks[index - 1];
    while (!week) {
      const next = weeks.length + 1;
      const startDate = addDays(setup.startDate, (next - 1) * DAYS_PER_WEEK);
      const rawEnd = addDays(setup.startDate, next * DAYS_PER_WEEK - 1);
      weeks.push({
        index: next,
        startDate,
        // The last bucket of a plan that fits is a partial week: it ends on
        // the deadline, not seven days after it opened.
        endDate:
          toUtcMs(startDate) <= toUtcMs(setup.deadline)
            ? earlierDate(rawEnd, setup.deadline)
            : rawEnd,
        minutes: 0,
        items: [],
      });
      week = weeks[index - 1];
    }
    return week;
  };

  let index = 1;
  let used = 0;
  for (const candidate of selected) {
    const startIndex = index;
    let remaining = candidate.cost;
    let first = true;

    if (weeklyMinutes <= 0) {
      // No weekly budget at all (you told us zero hours). Nothing can be
      // paced against it, so each entry gets its own bucket and the leg's
      // infeasibility carries the real message.
      const week = weekAt(index);
      week.minutes += remaining;
      week.items.push({
        entryId: candidate.entry.id,
        minutes: remaining,
        continued: false,
        continues: false,
      });
      index += 1;
    } else {
      do {
        const week = weekAt(index);
        const room = weeklyMinutes - used;
        if (room <= 0 && remaining > 0) {
          index += 1;
          used = 0;
          continue;
        }
        const take = Math.min(remaining, room);
        remaining -= take;
        used += take;
        week.minutes += take;
        week.items.push({
          entryId: candidate.entry.id,
          minutes: take,
          continued: !first,
          continues: remaining > 0,
        });
        first = false;
        if (remaining > 0) {
          index += 1;
          used = 0;
        }
      } while (remaining > 0);
    }

    const mandatory = mandatoryIds.has(candidate.entry.id);
    planned.push({
      entryId: candidate.entry.id,
      title: candidate.entry.title,
      orderIndex: candidate.orderIndex,
      leg: candidate.entry.leg,
      tier: candidate.entry.curation.tier,
      costMinutes: candidate.cost,
      value: candidate.value,
      mandatory,
      prerequisite: isRequiredByAnotherSelected(candidate.entry.id, selected),
      compressed: candidate.route.compressed,
      route: toPlannedRoute(candidate.route),
      weekIndex: startIndex,
    });
    selectedMinutes += candidate.cost;
    selectedValue += candidate.value;
  }

  const requiredHoursPerDay =
    daysLeft === 0 ? null : roundTo(selectedMinutes / MINUTES_PER_HOUR / daysLeft);
  const utilization =
    requiredHoursPerDay === null || availableHoursPerDay <= 0
      ? null
      : roundTo(requiredHoursPerDay / availableHoursPerDay);

  const lastWeek = weeks[weeks.length - 1];

  const plan: LegPlan = {
    id: setup.id,
    eventTitle: setup.eventTitle,
    startDate: setup.startDate,
    deadline: setup.deadline,
    daysLeft,
    hoursPerWeek: setup.hoursPerWeek,
    slack: setup.slack,
    capacityMinutes,
    feasible,
    method,
    exact: method !== "greedy-swap",
    candidateCount: setup.candidates.length,
    mandatoryCount: mandatoryIds.size,
    mandatoryMinutes,
    selected: planned,
    selectedCount: planned.length,
    selectedMinutes,
    selectedValue: roundTo(selectedValue),
    unusedMinutes: Math.max(0, capacityMinutes - selectedMinutes),
    deficitMinutes: Math.max(0, selectedMinutes - capacityMinutes),
    requiredHoursPerDay,
    utilization,
    weeks,
    projectedFinish: lastWeek ? lastWeek.endDate : null,
    remedies: [],
    cuts: [],
  };

  return {
    plan,
    selectedIds,
    mandatoryIds,
    mandatoryCompressible,
    mandatoryNonEssentialMinutes,
    mandatoryNonEssentialCount,
    fallback,
  };
}

/**
 * Is another planned entry built on this one? Drives the "watch this first"
 * affordance in the plan, and explains why a skippable entry is in a plan that
 * cut better ones.
 */
function isRequiredByAnotherSelected(id: EntryId, selected: readonly Candidate[]): boolean {
  return selected.some(
    (candidate) => candidate.entry.id !== id && candidate.entry.curation.prerequisites.includes(id),
  );
}

// ---------------------------------------------------------------------------
// The deterministic hash
// ---------------------------------------------------------------------------

/** FNV-1a, 32-bit, lowercase hex. Chosen because it is four lines in any language. */
export function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * The canonical string the hash is taken over. Integers and fixed-precision
 * decimals only — never a raw float, whose text form is a language's opinion
 * rather than a fact — so the Swift port can build the identical string.
 */
function canonicalize(result: Omit<PlanResult, "deterministicHash">): string {
  const parts: string[] = [
    `v=${result.solverVersion}`,
    `today=${result.today}`,
    `encore=${result.encore ? 1 : 0}`,
    `hpw=${result.hoursPerWeek.toFixed(4)}`,
    `slack=${result.slack.toFixed(4)}`,
    `w=${WEIGHT_KEYS.map((key) => result.weights[key].toFixed(4)).join(",")}`,
  ];
  for (const leg of result.legs) {
    parts.push(
      `leg=${leg.id}:${leg.startDate}:${leg.deadline}:${leg.daysLeft}:${leg.capacityMinutes}:${
        leg.feasible ? 1 : 0
      }:${leg.method}`,
    );
    parts.push(
      `sel=${leg.selected
        .map(
          (entry) =>
            `${entry.entryId}@${entry.costMinutes}@${entry.route?.kind ?? "full"}@${entry.weekIndex}`,
        )
        .join(",")}`,
    );
    parts.push(`weeks=${leg.weeks.map((week) => `${week.index}:${week.minutes}`).join(",")}`);
    parts.push(`rem=${leg.remedies.map((remedy) => remedy.kind).join(",")}`);
  }
  parts.push(`cuts=${result.cuts.map((cut) => `${cut.entryId}:${cut.reason}`).join(",")}`);
  parts.push(`warn=${result.warnings.map((warning) => `${warning.kind}`).join(",")}`);
  return parts.join("|");
}

// ---------------------------------------------------------------------------
// planWatchOrder
// ---------------------------------------------------------------------------

/** The splitting milestone (Endgame Encore), or `null` if the catalog has none. */
function splittingMilestone(catalog: Catalog): Milestone | null {
  return catalog.milestones.find((milestone) => milestone.splitAfterEntryId !== null) ?? null;
}

export function planWatchOrder(input: SolverInput): PlanResult {
  const { catalog, filters } = input;
  const weights = normalizeWeights(input.weights);
  const slack = normalizeSlack(input.slack);
  const hoursPerWeek = Math.max(0, input.hoursPerWeek);
  const watched = input.watched ?? {};
  const resolved = resolveFilters(catalog, filters);
  const score: ScoreContext = {
    weights,
    characters: resolved.characters,
    threads: resolved.threads,
  };

  const milestone = splittingMilestone(catalog);
  const encore = input.encoreMode && milestone !== null;

  const legIdFor = (entry: Entry): LegId =>
    encore ? (entry.leg === 1 ? "leg-1" : "leg-2") : "road";

  // Step 0 — hard filters. Everything that survives becomes a candidate priced
  // by steps 3 and 4; everything that doesn't becomes a Cut with its reason.
  const candidates: Candidate[] = [];
  const filteredCuts: Cut[] = [];
  let watchedCount = 0;
  let watchedMinutes = 0;

  const entries = [...catalog.entries].sort((a, b) => a.orderIndex - b.orderIndex);
  const entriesById = new Map(entries.map((entry) => [entry.id, entry] as const));
  for (const entry of entries) {
    const route = chooseRoute(entry, filters.useAlternatives);
    const candidate: Candidate = {
      entry,
      route,
      cost: route.minutes,
      value: entryValue(entry, score, route.valueRetention),
      orderIndex: entry.orderIndex,
      legId: legIdFor(entry),
    };

    if (watched[entry.id]) {
      watchedCount += 1;
      watchedMinutes += route.minutes;
      continue;
    }

    const reason = hardFilterReason(entry, resolved);
    if (reason) {
      filteredCuts.push(makeCut(candidate, reason));
      continue;
    }
    candidates.push(candidate);
  }

  // Step 1 — split into legs. Encore mode gives leg 1 the days between today
  // and the Encore, and leg 2 the days between the Encore and Doomsday: the
  // windows abut rather than overlap, which is what "leftover leg-1 capacity
  // does not roll forward" means in practice.
  const shared = {
    hoursPerWeek,
    slack,
    useAlternatives: filters.useAlternatives,
  };
  const setups: LegSetup[] =
    encore && milestone
      ? [
          {
            id: "leg-1",
            eventTitle: milestone.title,
            startDate: input.today,
            deadline: milestone.date,
            candidates: candidates.filter((candidate) => candidate.entry.leg === 1),
            ...shared,
          },
          {
            id: "leg-2",
            eventTitle: catalog.target.title,
            startDate: laterDate(input.today, milestone.date),
            deadline: catalog.target.releaseDate,
            candidates: candidates.filter((candidate) => candidate.entry.leg === 2),
            ...shared,
          },
        ]
      : [
          {
            id: "road",
            eventTitle: catalog.target.title,
            startDate: input.today,
            deadline: catalog.target.releaseDate,
            candidates,
            ...shared,
          },
        ];

  const solutions = setups.map(solveLeg);

  // Step 5, continued — remedies for any infeasible leg. `disable-encore`
  // needs to know what the single-road alternative would look like, so it is
  // computed here rather than inside the leg.
  const roadDays = Math.max(0, daysBetween(input.today, catalog.target.releaseDate));
  const roadCapacity = Math.max(
    0,
    Math.floor(roadDays * (hoursPerWeek / DAYS_PER_WEEK) * MINUTES_PER_HOUR * (1 - slack)),
  );
  const totalMandatoryMinutes = solutions.reduce(
    (sum, solution) => sum + solution.plan.mandatoryMinutes,
    0,
  );

  for (const solution of solutions) {
    if (solution.plan.feasible) continue;
    const { plan } = solution;
    const remedies: Remedy[] = [];

    if (!filters.useAlternatives && solution.mandatoryCompressible > 0) {
      remedies.push({
        kind: "enable-alternatives",
        savedMinutes: solution.mandatoryCompressible,
        sufficient: plan.mandatoryMinutes - solution.mandatoryCompressible <= plan.capacityMinutes,
      });
    }

    if (plan.daysLeft > 0) {
      remedies.push({
        kind: "raise-pace",
        hoursPerWeekNeeded: roundUpToHalf(
          (plan.mandatoryMinutes / MINUTES_PER_HOUR / plan.daysLeft) * DAYS_PER_WEEK,
        ),
        sufficient: true,
      });
    }

    if (encore && plan.id === "leg-1") {
      remedies.push({
        kind: "disable-encore",
        extraDays: Math.max(0, roadDays - plan.daysLeft),
        sufficient: totalMandatoryMinutes <= roadCapacity,
      });
    }

    remedies.push({
      kind: "lower-tier-ceiling",
      tierMax: 1,
      savedMinutes: solution.mandatoryNonEssentialMinutes,
      droppedCount: solution.mandatoryNonEssentialCount,
      sufficient:
        plan.mandatoryMinutes - solution.mandatoryNonEssentialMinutes <= plan.capacityMinutes,
    });

    plan.remedies = remedies;
  }

  // Step 8 — the cut list. Survivors that didn't make the plan, plus
  // everything the filters rejected, biggest sacrifice first.
  const selectedEverywhere = new Set<EntryId>();
  for (const solution of solutions) {
    for (const id of solution.selectedIds) selectedEverywhere.add(id);
  }

  const overBudget = new Map<LegId, Cut[]>();
  for (const setup of setups) overBudget.set(setup.id, []);
  for (const candidate of candidates) {
    if (selectedEverywhere.has(candidate.entry.id)) continue;
    overBudget.get(candidate.legId)?.push(makeCut(candidate, "over-budget"));
  }

  const cuts = [...filteredCuts];
  for (const solution of solutions) {
    const legCuts = [
      ...(overBudget.get(solution.plan.id) ?? []),
      ...filteredCuts.filter((cut) => cut.legId === solution.plan.id),
    ].sort(compareCuts);
    solution.plan.cuts = legCuts;
    cuts.push(...(overBudget.get(solution.plan.id) ?? []));
  }
  cuts.sort(compareCuts);

  // Warnings — the plan's own admissions.
  const warnings: PlanWarning[] = [];
  for (const solution of solutions) {
    const { plan } = solution;
    if (!plan.feasible) {
      warnings.push({
        kind: "infeasible-leg",
        legId: plan.id,
        deficitMinutes: plan.deficitMinutes,
        mandatoryCount: plan.mandatoryCount,
      });
    }
    if (plan.daysLeft === 0 && plan.selectedCount > 0) {
      warnings.push({ kind: "no-days-left", legId: plan.id });
    }
    if (plan.projectedFinish !== null) {
      const overrunDays = daysBetween(plan.deadline, plan.projectedFinish);
      if (overrunDays > 0) {
        warnings.push({
          kind: "schedule-overruns-deadline",
          legId: plan.id,
          projectedFinish: plan.projectedFinish,
          overrunDays,
        });
      }
    }
    if (solution.fallback) {
      warnings.push({
        kind: "approximate-fill",
        legId: plan.id,
        cause: solution.fallback.cause,
        overlappingEntryIds: solution.fallback.overlapping,
      });
    }
    for (const entry of plan.selected) {
      const source = entriesById.get(entry.entryId);
      if (!source) continue;
      const missing = source.curation.prerequisites.filter(
        (id) => !selectedEverywhere.has(id) && !watched[id],
      );
      if (missing.length > 0) {
        warnings.push({
          kind: "unmet-prerequisites",
          legId: plan.id,
          entryId: entry.entryId,
          missing,
        });
      }
    }
  }

  const legs = solutions.map((solution) => solution.plan);
  const totals: PlanTotals = {
    candidateCount: candidates.length,
    selectedCount: legs.reduce((sum, leg) => sum + leg.selectedCount, 0),
    selectedMinutes: legs.reduce((sum, leg) => sum + leg.selectedMinutes, 0),
    selectedHours: roundTo(
      legs.reduce((sum, leg) => sum + leg.selectedMinutes, 0) / MINUTES_PER_HOUR,
      2,
    ),
    capacityMinutes: legs.reduce((sum, leg) => sum + leg.capacityMinutes, 0),
    cutCount: cuts.length,
    cutMinutes: cuts.reduce((sum, cut) => sum + cut.costMinutes, 0),
    watchedCount,
    watchedMinutes,
  };

  const withoutHash: Omit<PlanResult, "deterministicHash"> = {
    solverVersion: SOLVER_VERSION,
    today: input.today,
    encore,
    hoursPerWeek,
    slack,
    weights,
    feasible: legs.every((leg) => leg.feasible),
    legs,
    cuts,
    warnings,
    totals,
  };

  return { ...withoutHash, deterministicHash: fnv1a(canonicalize(withoutHash)) };
}
